import React from 'react';
import { Card, Row, Col, Statistic, Tooltip, Spin, Alert, Empty } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useIndexerStatus } from '../../hooks/useIndexerManagement';
import { IndexerStatus } from '../../types/indexer';

/**
 * Component to display detailed indexer metrics
 */
const IndexerMetricsPanel: React.FC = () => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const { data: indexerStatus, isLoading, isError } = useIndexerStatus(30000);

  // Get running indexers with metrics
  const runningIndexers = React.useMemo(() => {
    if (!indexerStatus?.data) return [];

    return indexerStatus.data
      .filter((indexer: IndexerStatus) => indexer.status === 'running' && indexer.metrics)
      .sort((a: IndexerStatus, b: IndexerStatus) => a.blockchain.localeCompare(b.blockchain));
  }, [indexerStatus]);

  if (isLoading) {
    return (
      <Card title={indexerT('indexerMetrics', 'Indexer Metrics')} style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card title={indexerT('indexerMetrics', 'Indexer Metrics')} style={{ marginBottom: 16 }}>
        <Alert
          message={indexerT('errorMessages.title')}
          description={indexerT('errorMessages.description')}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (!runningIndexers.length) {
    return (
      <Card title={indexerT('indexerMetrics', 'Indexer Metrics')} style={{ marginBottom: 16 }}>
        <Empty description={indexerT('noRunningIndexers', 'No running indexers with metrics')} />
      </Card>
    );
  }

  return (
    <Card title={indexerT('indexerMetrics', 'Indexer Metrics')} style={{ marginBottom: 16 }}>
      {runningIndexers.map((indexer: IndexerStatus) => (
        <Card
          key={indexer.blockchain}
          title={indexer.blockchain.toUpperCase()}
          type="inner"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title={indexerT('blocksProcessedTooltip', 'Total number of blocks processed by this indexer')}>
                <Statistic
                  title={
                    <span>
                      {indexerT('blocksProcessed')}
                      <InfoCircleOutlined style={{ marginLeft: 8 }} />
                    </span>
                  }
                  value={indexer.metrics?.blocksProcessed || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title={indexerT('transactionsProcessedTooltip', 'Total number of transactions processed by this indexer')}>
                <Statistic
                  title={
                    <span>
                      {indexerT('transactionsProcessed')}
                      <InfoCircleOutlined style={{ marginLeft: 8 }} />
                    </span>
                  }
                  value={indexer.metrics?.transactionsProcessed || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title={indexerT('processingRateTooltip', 'Current block processing rate')}>
                <Statistic
                  title={
                    <span>
                      {indexerT('processingRate')}
                      <InfoCircleOutlined style={{ marginLeft: 8 }} />
                    </span>
                  }
                  value={indexer.metrics?.processingRate || 0}
                  suffix={indexerT('blocksPerSecond')}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title={indexerT('depositsDetectedTooltip', 'Total number of deposits detected by this indexer')}>
                <Statistic
                  title={
                    <span>
                      {indexerT('depositsDetected')}
                      <InfoCircleOutlined style={{ marginLeft: 8 }} />
                    </span>
                  }
                  value={indexer.metrics?.depositsDetected || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Tooltip>
            </Col>
          </Row>
        </Card>
      ))}
    </Card>
  );
};

export default IndexerMetricsPanel;
